import React, { useRef } from 'react';
import { createPortal } from 'react-dom';
import { nomeDaConta, programaDaConta, semNiveis, vantagensDaConta } from '../data/vantagensDaConta';
import { useTravarScroll, useFocoNoModal } from '../utils/travarScroll';
import { useFechavelComVoltar } from '../contexts/VoltarDoCelular';
import './VantagensDoCartao.css';

/**
 * "Vantagens desta conta": o que o programa do banco oferece no nível dela.
 *
 * Desenho: `docs/designs/vantagens-da-conta.md` (13/10/2026). A tabela e de
 * onde vem cada linha estão em `data/vantagensDaConta.ts`. Usa a mesma folha
 * de estilo do painel do cartão: é a mesma janela, com outra fonte.
 *
 * ── O texto informa e manda conferir ─────────────────────────────────────
 *
 * "O que o nível oferece", e nunca "você tem direito": voucher e desconto se
 * ativam no app do banco, com regra própria, e o banco troca os parceiros sem
 * avisar. As vantagens do nível DELA vêm primeiro, que é o que mudou por
 * último; as dos níveis anteriores, depois.
 */
interface Props {
  nome: string;
  programa?: string | null;
  nivel?: number | null;
  onClose: () => void;
}

const mesPorExtenso = (iso: string): string =>
  new Date(`${iso}T12:00:00`).toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });

const VantagensDaConta: React.FC<Props> = ({ nome, programa, nivel, onClose }) => {
  const caixa = useRef<HTMLDivElement>(null);
  useTravarScroll();
  useFechavelComVoltar(true, onClose);
  useFocoNoModal(caixa);
  const p = programaDaConta(programa);
  const vantagens = vantagensDaConta(programa, nivel);
  const niveis = Array.from(new Set(vantagens.map(v => v.nivel))).sort((a, b) => b - a);
  const proximo = p && nivel ? p.vantagens.filter(v => v.nivel === nivel + 1) : [];

  return createPortal(
    <div className="vdc-fundo" onClick={onClose}>
      <div
        className="vdc-caixa" role="dialog" aria-modal="true" aria-labelledby="vda-titulo"
        ref={caixa} tabIndex={-1} onClick={e => e.stopPropagation()}
        onKeyDown={e => { if (e.key === 'Escape') onClose(); }}
      >
        <div className="vdc-topo">
          <div>
            <h2 id="vda-titulo">Vantagens desta conta</h2>
            <p className="vdc-cartao">{nome}{p ? ` · ${nomeDaConta(programa, nivel)}` : ''}</p>
          </div>
          <button className="vdc-fechar" onClick={onClose} aria-label="Fechar">✕</button>
        </div>

        <div className="vdc-corpo">
          {!p || vantagens.length === 0 ? (
            <p className="vdc-nota">
              Ainda não temos as vantagens desta conta na nossa lista. Você encontra tudo no app do seu banco.
            </p>
          ) : (
            <>
              <p className="vdc-nota">
                {semNiveis(p)
                  ? <>O que o {p.nome} oferece, segundo a página do banco. </>
                  : <>O que o {p.programa} do {p.nome} oferece no seu nível, segundo a página do banco. </>}
                Cada benefício tem regra própria no app do banco, e os parceiros podem mudar: confira antes
                de contar com eles.
              </p>
              {niveis.map(n => (
                <section key={n}>
                  {!semNiveis(p) && <h3 className="vdc-tipo">{n === nivel ? `Nível ${n}, o seu` : `Nível ${n}`}</h3>}
                  <ul className="vdc-lista">
                    {vantagens.filter(v => v.nivel === n).map(v => (
                      <li key={v.id}><strong>{v.nome}</strong></li>
                    ))}
                  </ul>
                </section>
              ))}
              {proximo.length > 0 && (
                <section>
                  <h3 className="vdc-tipo">No próximo nível</h3>
                  <ul className="vdc-lista">
                    {proximo.map(v => <li key={v.id}><span>{v.nome}</span></li>)}
                  </ul>
                </section>
              )}
              {p.pacote && (
                <section>
                  <h3 className="vdc-tipo">O que a conta inclui por mês</h3>
                  <ul className="vdc-lista">
                    {p.pacote.itens.map(i => <li key={i}><span>{i}</span></li>)}
                  </ul>
                  <p className="vdc-nota" style={{ marginTop: 10 }}>
                    {p.pacote.nome}, segundo os dados abertos do banco. {p.pacote.nota}
                  </p>
                </section>
              )}
              <p className="vdc-rodape">
                Conferido em {mesPorExtenso(p.revisadoEm)}.{' '}
                <a href={p.fonte} target="_blank" rel="noopener noreferrer">Ver na página do banco ↗</a>
              </p>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default VantagensDaConta;
